import { useQuery } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// Types matching backend responses
export interface StoryListItem {
  storyId: string;
  genre: string;
  title: string;
  description: string;
  status: string;
  totalNodes: number;
  totalPlays: number;
  createdAt: string;
}

export interface StoryDetails {
  storyId: string;
  genre: string;
  title: string;
  description: string;
  rootNodeId: string;
  status: string;
  totalNodes: number;
  totalPlays: number;
  createdAt: string;
  updatedAt: string;
}

interface ListStoriesResponse {
  stories: StoryListItem[];
}

interface UserStoryProgressResponse {
  storyIds: string[];
}

// Query keys for cache management
export const storyKeys = {
  all: ['stories'] as const,
  lists: () => [...storyKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...storyKeys.lists(), filters] as const,
  details: () => [...storyKeys.all, 'detail'] as const,
  detail: (id: string) => [...storyKeys.details(), id] as const,
  userProgress: (userId: string) => ['user', userId, 'story-progress'] as const,
};

/**
 * Fetch all published stories (public endpoint)
 */
export function useStories() {
  return useQuery({
    queryKey: storyKeys.list({}),
    queryFn: async () => {
      const response = await api.get<ListStoriesResponse>('/stories');
      return response.data.stories;
    },
  });
}

/**
 * Fetch a single story by ID
 */
export function useStory(storyId: string | undefined) {
  return useQuery({
    queryKey: storyKeys.detail(storyId ?? ''),
    queryFn: async () => {
      const response = await api.get<StoryDetails>(`/stories/${storyId}`);
      return response.data;
    },
    enabled: !!storyId,
  });
}

/**
 * Fetch list of storyIds where current user has reading progress
 * @param userId - Current user's ID (used to scope the query cache)
 */
export function useUserStoryProgress(userId: string | undefined) {
  return useQuery({
    queryKey: storyKeys.userProgress(userId ?? ''),
    queryFn: async () => {
      const response = await api.get<UserStoryProgressResponse>('/user/story-progress');
      return new Set(response.data.storyIds);
    },
    staleTime: 30000, // Consider fresh for 30 seconds
    enabled: !!userId, // Only fetch when user is logged in
  });
}
